import axios from 'axios';
import router from '../router/index.js';
import store from '../store/index.js';
import { loadProgressBar } from "axios-progress-bar";

let logout_timer = null;
const INACTIVITY_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds

export function reset_logout_timer() {
    if (logout_timer) {
        clearTimeout(logout_timer);
    }
    // Only set the timer if the user is logged in (check Vuex state)
    if (store.state.is_logged_in) {
        logout_timer = setTimeout(() => {
            console.log("Inactivity timeout reached. Logging out.");
            store.dispatch('logout');
            router.push('/login')
        }, 
        INACTIVITY_TIMEOUT);
    }
}

// Function to clear the timer explicitly, e.g., on manual logout
export function clear_logout_timer() {
    if (logout_timer) {
        clearTimeout(logout_timer);
        logout_timer = null;
    }
}

const api_client = axios.create({
    responseType: "json",
    baseURL: 'http://devgui.bisonrouter.com/api/',
    withCredentials: false,
    headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
    }
});

// Request interceptor: Reset timer on every request
api_client.interceptors.request.use(
    config => {
        reset_logout_timer();
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Response interceptor: Reset timer on successful response, handle errors
api_client.interceptors.response.use(
    response => {
        reset_logout_timer(); // Reset timer on successful response
        return response;
    },
    error => {
        console.log(error)

        // If we get 401 Unauthorized, we can handle it:
        if (error.response && error.response.status === 401) {
            // For instance, log out the user or refresh the token
            console.warn('Token might be invalid or expired');
            // run logout
            store.dispatch('logout');
            // Redirect to login page
            router.push('/login')
        }

        return Promise.reject(error)
    }
);

loadProgressBar({
        minimum: 0.08,
        easing: 'linear',
        positionUsing: '',
        speed: 200,
        trickle: true,
        trickleSpeed: 200,
        showSpinner: true,
        barSelector: '[role="bar"]',
        spinnerSelector: '[role="spinner"]',
        parent: 'body',
        template: '<div class="bar" role="bar"><div class="peg"></div></div><div class="spinner" role="spinner"><div class="spinner-icon"></div></div>'
    }, 
    api_client
);

export default {
    /*
     * VIFs
     */
    sh_vifs() {
        return api_client.get('/vif/show');
    },
    sh_vif_with_name(data) {
        return api_client.post('/vif/show_with_name', data);
    },
    del_vif(data) {
        return api_client.post('/vif/del', data);
    },
    add_vif(data) {
        return api_client.post('/vif/add', data);
    },

    /*
     * IPv4 addresses
     */
    sh_vif_ip_addrs() {
        return api_client.get('/vif_ipv4_addr/show');
    },
    sh_vif_ip_addr(data) {
        return api_client.post('/vif_ipv4_addr/show_addr', data);
    },
    del_vif_ip_addr(data) {
        return api_client.post('/vif_ipv4_addr/del', data);
    },
    add_vif_ip_addr(data) {
        return api_client.post('/vif_ipv4_addr/add', data);
    },

    /*
     * IPv4 route tables
     */
    sh_ipv4_route_tables() {
        return api_client.get('/ipv4_route_table/show');
    },
    add_ipv4_route_table(data) {
        return api_client.post('/ipv4_route_table/add', data);
    },
    del_ipv4_route_table(data) {
        return api_client.post('/ipv4_route_table/del', data);
    },    

    /*
     * IPv6 route tables
     */
    sh_ipv6_route_tables() {
        return api_client.get('/ipv6_route_table/show');
    },
    add_ipv6_route_table(data) {
        return api_client.post('/ipv6_route_table/add', data);
    },
    del_ipv6_route_table(data) {
        return api_client.post('/ipv6_route_table/del', data);
    },
    
    /*
     * IPv4 routes
     */
    sh_ipv4_routes(data) {
        return api_client.post('/ipv4_route/show', data);
    },
    sh_ipv4_route(data) {
        return api_client.post('/ipv4_route/show_route', data);
    },
    add_connected_ipv4_route(data) {
        return api_client.post('ipv4_route/add_connected', data);
    },
    del_ipv4_route(data) {
        return api_client.post('ipv4_route/del', data);
    },
    add_ipv4_route(data) {
        return api_client.post('ipv4_route/add', data);
    },
    add_ipv4_multi_hop_route(data) {
        return api_client.post('ipv4_route/add_multi_hop', data);
    },
    add_ipv4_single_hop_route(data) {
        return api_client.post('ipv4_route/add_single_hop', data);
    },

    /*
     * Radius
     */
    sh_radius_clients() {
        return api_client.get('/radius/show');
    },
    radius_add_src_ip(data) {
        return api_client.post('/radius/add_src_ip', data);
    },
    radius_add_server(data) {
        return api_client.post('/radius/add_server', data);
    },
    radius_del_server(data) {
        return api_client.post('/radius/del_server', data);
    },
    radius_add_client(data) {
        return api_client.post('/radius/add', data);
    },
    radius_set_secrets(data) {
        return api_client.post('/radius/set_secret', data);
    },

    /*
     * IP pool
     */
    sh_ip_pools() {
        return api_client.get('/ip_pool/show');
    },
    add_ip_pool(data) {
        return api_client.post('/ip_pool/create', data);
    },
    del_ip_pool(data) {
        return api_client.post('/ip_pool/destroy', data);
    },
    add_ip_pool_range(data) {
        return api_client.post('/ip_pool/add_range', data);
    },
    del_ip_pool_range(data) {
        return api_client.post('/ip_pool/del_range', data);
    },
    add_pool_server(data) {
        return api_client.post('/ip_pool/add_server', data);
    },
    del_pool_server(data) {
        return api_client.post('/ip_pool/del_server', data);
    },
    set_dhcp_options(data) {
        return api_client.post('/ip_pool/set_dhcp_options', data);
    },

    /*
     * IP pool
     */
    sh_ipv6_pools() {
        return api_client.get('/ipv6_pool/show');
    },

    /*
     * PPPoE profile
     */
    sh_pppoe_profiles() {
        return api_client.get('/pppoe_profile/show');
    },
    add_pppoe_profile(data) {
        return api_client.post('/pppoe_profile/add', data);
    },
    del_pppoe_profile(data) {
        return api_client.post('/pppoe_profile/del', data);
    },
    pppoe_profile_add_ip_pool(data) {
        return api_client.post('/pppoe_profile/add_ip_pool', data);
    },
    pppoe_profile_del_ip_pool(data) {
        return api_client.post('/pppoe_profile/del_ip_pool', data);
    },
    pppoe_profile_add_service_name(data) {
        return api_client.post('/pppoe_profile/add_service_name', data);
    },
    pppoe_profile_del_service_name(data) {
        return api_client.post('/pppoe_profile/del_service_name', data);
    },
    pppoe_profile_add_vlan_set(data) {
        return api_client.post('/pppoe_profile/add_vlan_set', data);
    },
    pppoe_profile_del_vlan_set(data) {
        return api_client.post('/pppoe_profile/del_vlan_set', data);
    },
    pppoe_profile_set_opts(data) {
        return api_client.post('/pppoe_profile/set_opts', data);
    },

    /*
     * VLAN set
     */
    sh_vlan_sets() {
        return api_client.get('/vlan_set/show');
    },

    /*
     * PPPoE settings
     */
    sh_pppoe_settings() {
        return api_client.get('/pppoe/settings');
    },

    sh_pppoe_ip_pools() {
        return api_client.get('/pppoe/show_ip_pools');
    },

    pppoe_add_ip_pool(data) {
        return api_client.post('/pppoe/add_ip_pool', data);
    },
    
    pppoe_del_ip_pool(data) {
        return api_client.post('/pppoe/del_ip_pool', data);
    },

    /*
     * Sysctl
     */
    systl_set(data) {
        return api_client.post('/sysctl/set', data);
    },

    systl_set_w_dq(data) {
        return api_client.post('/sysctl/set_w_dq', data);
    },

    run_post_query(url, data) {
        return api_client.post(url, data);
    },

    run_get_query(url, params) {
        return api_client.get(url, { params });
    },

    run_delete_query(url, data) {
        return api_client.delete(url, { data });
    }
}

export function set_auth_token_hdr(auth_token_str) {
    if (auth_token_str) {
        api_client.defaults.headers.common['Authorization'] = `Bearer ${auth_token_str}`;
    }
    else {
        delete api_client.defaults.headers.common['Authorization'];
    }
}
